import {
	useApiGetAiTokenUsageMetrics,
	useApiGetAiTokenUsageTimeseries,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import { MetricsAiCostUsageCards } from './components/cards/MetricsAiCostUsageCards.tsx';
import { MetricsAiTokenUsageCards } from './components/cards/MetricsAiTokenUsageCards';
import { AiCostUsageChart } from './components/charts/AiCostUsageChart';
import { AiTokenUsageChart } from './components/charts/AiTokenUsageChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { aiDisplayAsFilterOptions, aiGroupByFilterOptions } from './constants';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type {
	AiCostUsageMetricCardType,
	AiDisplayAsType,
	AiGroupByType,
	AiTokenUsageMetricCardType,
	GenericOverviewTabControllerProps,
} from './types';

import { isValidTimeRange, isValidViewOption } from './types.ts';
export const AiTokenUsageOverviewTabController = memo(
	({ onMetricCardClick }: GenericOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'ai-token-usage' });
		const navigate = useNavigate();
		const [groupByFilter, setGroupByFilter] =
			useState<AiGroupByType>('user_id');
		const [displayAsFilter, setDisplayAsFilter] =
			useState<AiDisplayAsType>('tokens');
		const [showAllUsersInTooltip, setShowAllUsersInTooltip] = useState(false);

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
					trackEvent(
						'ai_token_usage_settings/date_filter_change',
						{
							time_range: value,
						},
						user,
						workspace
					);
				}
			},
			[setDateFilter, user, workspace]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
					trackEvent(
						'ai_token_usage_settings/view_option_change',
						{
							view_option: value,
						},
						user,
						workspace
					);
				}
			},
			[setDateRangeOption, user, workspace]
		);

		const { data: metrics, isLoading: isMetricsLoading } =
			useApiGetAiTokenUsageMetrics({
				queryParams: {
					time_range: dateFilter,
				},
			});

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetAiTokenUsageTimeseries(
				{
					queryParams: {
						time_range: dateFilter,
						group_by: groupByFilter,
						view_option: dateRangeOption,
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const handleGroupByFilterChange = useCallback(
			(filterTypes: string[]) => {
				const newGroupByFilter = filterTypes[0] as AiGroupByType;
				setGroupByFilter(newGroupByFilter);

				const filterLabel = aiGroupByFilterOptions.find(
					(opt) => opt.value === newGroupByFilter
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'ai_token_usage_settings/group_by_filter_change',
					{
						group_by_filter: filterLabel,
						group_by_value: newGroupByFilter,
					},
					user,
					workspace
				);
			},
			[user, workspace]
		);

		const handleDisplayAsFilterChange = useCallback(
			(filterTypes: string[]) => {
				const newDisplayAsFilter = filterTypes[0] as AiDisplayAsType;
				setDisplayAsFilter(newDisplayAsFilter);

				const filterLabel = aiDisplayAsFilterOptions.find(
					(opt) => opt.value === newDisplayAsFilter
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'ai_token_usage_settings/display_as_filter_change',
					{
						display_as_filter: filterLabel,
						display_as_value: newDisplayAsFilter,
					},
					user,
					workspace
				);
			},
			[user, workspace]
		);

		const handleNavigate = useCallback(() => {
			trackEvent(
				'ai_token_usage_settings/analytics_banner_click',
				{},
				user,
				workspace
			);
			navigate(`/analytics`);
		}, [navigate, user, workspace]);

		const handleMetricCardClick = useCallback(
			(metricType: AiTokenUsageMetricCardType | AiCostUsageMetricCardType) => {
				trackEvent(
					'ai_token_usage_settings/metric_card/click',
					{
						metric_type: metricType,
						display_as: displayAsFilter,
						time_range: dateFilter || '24h',
					},
					user,
					workspace
				);
				onMetricCardClick?.(metricType);
			},
			[displayAsFilter, dateFilter, user, workspace, onMetricCardClick]
		);

		if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
			return <MetricsOverviewTabSkeleton />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[aiGroupByFilterOptions, aiDisplayAsFilterOptions]}
				filterTypes={[groupByFilter, displayAsFilter]}
				setFilterTypes={(filterTypes: string[]) => {
					// Handle both filters based on their position
					if (filterTypes[0] !== groupByFilter) {
						handleGroupByFilterChange([filterTypes[0]]);
					}
					if (filterTypes[1] !== displayAsFilter) {
						handleDisplayAsFilterChange([filterTypes[1]]);
					}
				}}
				showAllUsersInTooltip={showAllUsersInTooltip}
				setShowAllUsersInTooltip={setShowAllUsersInTooltip}
			>
				{displayAsFilter === 'tokens' ? (
					<MetricsAiTokenUsageCards
						aiTokenUsageMetrics={metrics}
						onMetricCardClick={handleMetricCardClick}
					/>
				) : (
					<MetricsAiCostUsageCards
						aiTokenUsageMetrics={metrics}
						onMetricCardClick={handleMetricCardClick}
					/>
				)}
				<AnalyticsBanner handleNavigate={handleNavigate} />
				{displayAsFilter === 'tokens' ? (
					<AiTokenUsageChart
						timeseries={timeseries}
						dateFilter={dateFilter || '24h'}
						setDateFilter={handleSetDateFilter}
						setTimeRangeValue={setTimeRangeValue}
						timeRangeValue={timeRangeValue}
						dateRangeOption={dateRangeOption || 'day'}
						setDateRangeOption={handleSetDateRangeOption}
						showAllUsersInTooltip={showAllUsersInTooltip}
					/>
				) : (
					<AiCostUsageChart
						timeseries={timeseries}
						dateFilter={dateFilter || '24h'}
						setDateFilter={handleSetDateFilter}
						setTimeRangeValue={setTimeRangeValue}
						timeRangeValue={timeRangeValue}
						dateRangeOption={dateRangeOption || 'day'}
						setDateRangeOption={handleSetDateRangeOption}
						showAllUsersInTooltip={showAllUsersInTooltip}
					/>
				)}
			</MetricsOverviewTab>
		);
	}
);
